// MobileMenu — the collapsed (<md) navigation panel for the site Header. Rendered
// as an overlay + slide-down sheet under the header bar; the header owns the open
// state and the hamburger toggle (aria-expanded / aria-controls live there).
//
// Accessibility: the panel is labelled, closes on Escape, and restores body scroll
// when dismissed. A focus trap is intentionally NOT implemented (per spec); Escape
// + click-outside cover the common dismissals. Motion honors prefers-reduced-motion
// via the shared `reducedMotion` flag.
import { useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import type { AppLocale } from "../i18n";
import { reducedMotion } from "../motion/variants";
import LanguageSwitcher from "./LanguageSwitcher";

export interface MobileMenuProps {
  open: boolean;
  /** Close the menu (also re-baselines the header's aria-expanded). */
  onClose: () => void;
  /** Active locale prefix, for building links. */
  current: AppLocale;
  /** Nav path segments in order ("" = locale home). */
  segments: readonly string[];
  /** Translator bound to the `common` namespace. */
  t: (key: string) => string;
  /** id matching the header toggle's aria-controls. */
  id: string;
}

export default function MobileMenu({
  open,
  onClose,
  current,
  segments,
  t,
  id,
}: MobileMenuProps) {
  // Esc-to-close + lock body scroll while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const dur = reducedMotion ? 0.001 : 0.25;
  const ease = [0.16, 1, 0.3, 1] as const;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim below the header bar; clicking it closes the sheet. */}
          <motion.div
            className="fixed inset-0 top-16 z-40 bg-[var(--ink)]/20 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: dur }}
            aria-hidden="true"
            onClick={onClose}
          />

          <motion.nav
            id={id}
            aria-label={t("header.menu")}
            className="absolute inset-x-0 top-full z-50 origin-top border-b border-[var(--brand-tint)] bg-white shadow-[var(--shadow-card)] md:hidden"
            initial={{ opacity: 0, y: reducedMotion ? 0 : -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reducedMotion ? 0 : -8 }}
            transition={{ duration: dur, ease }}
          >
            <ul className="flex flex-col px-6 py-4">
              {segments.map((seg) => (
                <li key={seg || "home"}>
                  <NavLink
                    to={`/${current}${seg ? `/${seg}` : ""}`}
                    end={seg === ""}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `block rounded-[var(--radius-md)] px-3 py-3 text-[var(--text-lg)] font-medium transition-colors ${
                        isActive
                          ? "bg-[var(--brand-tint-3)] text-[var(--brand-azure-text)]"
                          : "text-[var(--ink)] hover:bg-[var(--brand-tint-3)] hover:text-[var(--brand-azure-text)]"
                      }`
                    }
                  >
                    {t(`nav.${seg || "home"}`)}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-between gap-4 border-t border-[var(--brand-tint)] px-6 py-4">
              <LanguageSwitcher onSwitch={onClose} />
              <Link
                to={`/${current}/contact`}
                onClick={onClose}
                className="inline-flex items-center rounded-[var(--radius-md)] bg-[var(--brand-primary)] px-5 py-2.5 text-[var(--text-base)] font-medium text-white shadow-[var(--shadow-card)] transition-colors hover:bg-[var(--brand-azure)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]"
              >
                {t("header.cta")}
              </Link>
            </div>
          </motion.nav>
        </>
      )}
    </AnimatePresence>
  );
}
